import React from 'react';
import DatePicker from 'react-datepicker';
import { Portal } from 'react-overlays';
import dateTimeHelper from '../../../Helpers/datetimeHelper';
import CustomDateInput from './CustomDateInput';

// Fallback id source for callers that don't pass a fieldName. The picker's DOM ids used to be the
// hard-coded literal 'caspiandatepicker', so every date field on a page shared one id — invalid HTML,
// and it made the component's own <label htmlFor> point at whichever picker happened to render first.
let nextPickerId = 1;

class CaspianDatePicker extends React.Component {
  static defaultProps = {
    selected: new Date(),
    allowIndefinite: false,
    onChange: () => alert('Empty function'),
    labelText: '',
    disabled: false,
    fieldName: '',
    // Wording for the indefinite toggle, surfaced as its tooltip/accessible name. The VISIBLE label is
    // always the ∞ glyph: a worded one cannot fit a narrow field, and ∞ reads correctly in both senses
    // this control is used for — "no end date" on an entity, "no bound" on a search range.
    indefiniteLabel: 'No limit',
    // When true, the picker operates purely on the calendar day and never
    // re-interprets the wall-clock time as UTC. This keeps the displayed and
    // returned date identical in every timezone (UTC-minus, UTC and UTC-plus),
    // which is what the backend's date-only / UTC-midnight columns expect.
    utc: false,
    // When true, a null value renders an empty picker instead of falling back to today.
    allowEmpty: false,
  };

  constructor(props) {
    super(props);
    let firstSelectedDate = props.selected;

    // Per-instance, stable for the component's lifetime, so the label/input pairing below is unique
    // even when several date fields share a page.
    this.domId = `caspiandatepicker-${props.fieldName || `f${nextPickerId++}`}`;

    this.state = {
      selectedDate: firstSelectedDate,
      labelText: props.labelText,
      indefinite: dateTimeHelper.isIndefinite(firstSelectedDate),
      prevSelectedDate: new Date(),
    };
  }

  CalendarContainer = ({ children }) => {
    const el = document.getElementById('calendar-portal');
    return <Portal container={el}>{children}</Portal>;
  };

  componentDidMount() {}

  // The selected day lives in this component's own state, so a `selected` changed from the outside (a replaced
  // item, the side-by-side copy button) has to be adopted. It may be a Date OR a date string, which is why the
  // value is parsed before it is compared — the old `!isNaN(selected)` guard dropped every string date.
  componentDidUpdate(prevProps) {
    if (this.props.selected === prevProps.selected) {
      return;
    }
    this.setState({
      selectedDate: CaspianDatePicker.asDateOrNull(this.props.selected),
      indefinite: dateTimeHelper.isIndefinite(this.props.selected),
    });
  }

  // Date | date string | '' | null | undefined -> a Date, or null (what render() treats as "no date yet").
  static asDateOrNull(value) {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const date = value instanceof Date ? value : new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }

  renderIndefiniteCheckBox() {
    if (!this.props.allowIndefinite) {
      return null;
    }

    const toggleId = `${this.domId}-indefinite`;
    const label = this.props.indefiniteLabel;

    return (
      <span
        className='ds-dateinf-toggle'
        title={label}
      >
        <input
          id={toggleId}
          type='checkbox'
          checked={this.state.indefinite}
          aria-label={label}
          onChange={(e) => {
            var newIndefinite = !this.state.indefinite;
            var selDate = newIndefinite ? dateTimeHelper.indefiniteDateValue() : this.state.prevSelectedDate;
            var prevSelDate = this.state.selectedDate;
            this.setState({
              indefinite: e.target.checked,
              selectedDate: selDate,
              prevSelectedDate: prevSelDate,
            });
            this.props.onChange(selDate);
          }}
        />
        <label htmlFor={toggleId}>&#8734;</label>
      </span>
    );
  }

  render() {
    let sel = null;
    if (this.state.selectedDate || !this.props.allowEmpty) {
      sel = this.state.selectedDate ? this.state.selectedDate : new Date();
      sel = new Date(sel);

      if (isNaN(sel.getTime())) {
        //react-datepicker formats `selected` with date-fns, which throws 'Invalid time value' on an
        //unparsable date - and that takes the whole page down through the error boundary. Falling back
        //to today keeps the field (and the panel around it) usable (CD-5517).
        sel = new Date();
      }

      if (this.props.utc) {
        //Display the calendar day as-is (local midnight), independent of timezone.
        sel = new Date(sel.getFullYear(), sel.getMonth(), sel.getDate());
      } else if (sel.getHours() !== 0) {
        //If hour component is not 0, the date is more than likely in locale time, so convert it to UTC
        sel = new Date(sel.getTime() + sel.getTimezoneOffset() * 60000);
      }
    }

    return (
      <>
        {this.state.labelText !== '' && (
          <>
            <label htmlFor={this.domId}>{this.state.labelText}</label>
            <br />
          </>
        )}
        {/* The indefinite modifier goes on the FIELD, not on the trigger: react-datepicker wraps its
            custom input in a .react-datepicker-wrapper, so the trigger is not a sibling of the toggle
            and no sibling selector can reach it. Sizing rules hang off this class, which a plain date
            field never gets. */}
        <span className={`ds-datefield${this.props.allowIndefinite ? ' ds-datefield-indefinite' : ''}`}>
          {this.renderIndefiniteCheckBox()}
          {
            !this.state.indefinite ? (
              <DatePicker
                name={this.domId}
                className=' initialism '
                selected={sel}
                onChange={(e) => {
                  //react-datepicker emits a local-midnight Date for the picked calendar day.
                  //In utc mode pass that day straight through; re-interpreting the wall-clock as
                  //UTC (the legacy path) makes the date slip to the previous day in UTC-minus zones.
                  const date = this.props.utc
                    ? new Date(e.getFullYear(), e.getMonth(), e.getDate())
                    : dateTimeHelper.convertUTCDateStringToLocale(e);
                  this.setState({
                    selectedDate: date,
                  });
                  this.props.onChange(date);
                }}
                disabledNavigation
                disabled={this.props.disabled}
                calendarClassName='ds-datecal'
                customInput={<CustomDateInput />}
                showMonthDropdown
                showYearDropdown
                todayButton='Today'
                id={this.domId}
                popperContainer={this.CalendarContainer}
                popperPlacement='top-start'
                popperModifiers={{
                  //popper v1 syntax (react-datepicker 3.8 → react-popper 1.x). Without this the
                  //calendar keeps its flipped bottom placement on zoomed/short viewports and runs
                  //off-screen; shifting within the viewport keeps it fully visible instead.
                  preventOverflow: {
                    enabled: true,
                    escapeWithReference: false,
                    boundariesElement: 'viewport',
                  },
                }}
              />
            ) : (
              <span className='ds-dateindefinite'>&#8734;</span>
            ) //indefinite symbol
          }
        </span>
      </>
    );
  }
}

export default CaspianDatePicker;
